import { usePatientNextAppointment } from 'infrastructure/hooks/patient/use-patient-next-appointment';
import { useDoctorAndAgencyDoctorSelect } from 'infrastructure/hooks/select/use-doctor-and-agency-doctor';
import { useCallback, useEffect, useState } from 'react';
import { useAppointmentCompletion } from 'infrastructure/hooks/patient/use-patient-appointment-completion';
import { utcToZonedTime } from 'date-fns-tz';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import { NextAppointment } from 'infrastructure/classes/next-appointment';

import { generateMonthlyInteractionCallMessage } from '../blurbs';

import type { AppointmentMethod } from 'infrastructure/enums';
import type {
  IAppointmentCompletion,
  IDoctorAndAgencyDoctorSelect,
  INextAppointment,
} from 'infrastructure/interfaces';
import type {
  IMonthlyInteractionCallValues,
  IUseMonthlyInteractionCallProps,
} from '../types';

function getRecentUpcomingAppointmentsDate(
  nextAppointment?: void | INextAppointment,
  appointmentCompletionData?: IAppointmentCompletion[],
): { date?: string; time?: string } {
  let date: string | undefined;
  let time: string | undefined;

  const sourceDate =
    nextAppointment?.scheduledDate ||
    appointmentCompletionData?.[0]?.completedDate;

  if (sourceDate) {
    const parsed = new Date(sourceDate);
    date = utcToZonedTime(parsed, 'UTC').toString();
    time = DateFormatter({
      date: utcToZonedTime(parsed, 'UTC'),
      format: DateFormats['HH:mm'],
    });
  }

  return { date, time };
}

export const useMonthlyInteractionCall = ({
  closeModal,
  handleSubmit,
  whichPerson,
  otherPerson,
  patientGuid,
}: IUseMonthlyInteractionCallProps) => {
  const { nextAppointment } = usePatientNextAppointment({
    patientGuid,
  });
  const { appointmentCompletionData } = useAppointmentCompletion({
    patientGuid,
    meta: {},
  });
  const [doctor, setDoctor] = useState<string>('');
  const [doctorGuid, setDoctorGuid] = useState<string>('');
  const [appointmentMethod, setappointmentMethod] = useState<
    string | undefined
  >();
  const [doctorType, setDoctorType] =
    useState<IDoctorAndAgencyDoctorSelect['type']>();
  const { loadDoctorsAndAgencyDoctors } = useDoctorAndAgencyDoctorSelect({
    patientGuid,
  });
  const loadDoctors = async (assignedDoctorGuid: string) => {
    const doctors = await loadDoctorsAndAgencyDoctors({
      guid: assignedDoctorGuid,
      page: 1,
      items: 1,
      totalCount: 0,
    });
    setDoctor(doctors[0].label);
    setDoctorGuid(doctors[0].guid);
    setDoctorType(doctors[0].type);
  };

  useEffect(() => {
    if (nextAppointment?.doctorGuid) loadDoctors(nextAppointment.doctorGuid);
    else if (nextAppointment?.agencyDoctorGuid)
      loadDoctors(nextAppointment.agencyDoctorGuid);
    else {
      setDoctor(appointmentCompletionData?.items[0]?.completedBy || '');
    }
    if (nextAppointment?.appointmentMethod) {
      setappointmentMethod(nextAppointment.appointmentMethod);
    }
  }, [
    nextAppointment?.doctorGuid,
    nextAppointment?.agencyDoctorGuid,
    appointmentCompletionData?.items[0]?.completedBy,
  ]);

  const recentAppointmentDateTime = getRecentUpcomingAppointmentsDate(
    nextAppointment,
    appointmentCompletionData?.items,
  );

  const initialValues: IMonthlyInteractionCallValues = {
    whichPerson,
    otherPerson,
    isPatientHaveExpEtc: '',
    accessToReadingHealtcareCheckbox: [],
    educationProvided: [],
    medicationChangesQuestionsCheckbox: [],
    recentUpcomingAppointmentsProvider: doctor,
    recentUpcomingAppointmentsProviderGuid: doctorGuid,
    recentUpcomingAppointmentsProviderType: doctorType,
    recentUpcomingAppointmentsMethod: appointmentMethod,
    recentUpcomingAppointmentsGuid: nextAppointment?.guid,
    recentUpcomingAppointmentsTime: recentAppointmentDateTime.time,
    recentUpcomingAppointmentsDate: recentAppointmentDateTime.date,
    patientGuid,
    recentUpcomingAppointments: undefined,
  };

  const selectAppointmentMethod = useCallback(
    (data: {
      appointment?: {
        appointmentMethod?: string;
      };
      recentUpcomingAppointmentsMethod?: string;
    }) =>
      data?.appointment?.appointmentMethod ??
      data?.recentUpcomingAppointmentsMethod ??
      '',
    [],
  );

  const selectAppointmentPayload = useCallback(
    (data: {
      appointment?: {
        guid?: string;
        appointmentMethod?: string;
      };
      recentUpcomingAppointmentsGuid?: string;
      recentUpcomingAppointmentsDate?: Date | string;
      recentUpcomingAppointmentsTime?: string;
      recentUpcomingAppointmentsProviderType?: string;
      recentUpcomingAppointmentsProviderGuid?: string;
      recentUpcomingAppointments?: string;
    }): NextAppointment | null => {
      const {
        appointment,
        recentUpcomingAppointmentsGuid,
        recentUpcomingAppointmentsDate,
        recentUpcomingAppointmentsTime,
        recentUpcomingAppointmentsProviderType,
        recentUpcomingAppointmentsProviderGuid,
        recentUpcomingAppointments,
      } = data || {};
      if (
        !recentUpcomingAppointmentsDate ||
        !recentUpcomingAppointmentsTime ||
        recentUpcomingAppointments !== 'yes'
      ) {
        return null;
      }

      const guid =
        appointment?.guid ?? recentUpcomingAppointmentsGuid ?? undefined;

      const isoDate = (() => {
        if (recentUpcomingAppointmentsDate instanceof Date) {
          return recentUpcomingAppointmentsDate.toISOString().slice(0, 10);
        }
        const date = String(recentUpcomingAppointmentsDate);
        return date.includes('T') ? date.split('T')[0] : date;
      })();

      const time = recentUpcomingAppointmentsTime?.trim();
      if (!time) return null;

      const method = selectAppointmentMethod(data);
      if (!method) return null;

      const appointmentAgencyDoctorGuid =
        recentUpcomingAppointmentsProviderType === 'agencyDoctor'
          ? recentUpcomingAppointmentsProviderGuid
          : undefined;

      const appointmentDoctorGuid =
        recentUpcomingAppointmentsProviderType === 'doctor'
          ? recentUpcomingAppointmentsProviderGuid
          : undefined;

      const scheduledDate = (() => {
        if (nextAppointment) {
          const helper = new NextAppointment(patientGuid, nextAppointment);
          return helper.prepareDate(
            new Date(recentUpcomingAppointmentsDate),
            recentUpcomingAppointmentsTime,
          );
        }
        return `${isoDate}T${time}`;
      })();

      const payload: INextAppointment = {
        guid: String(guid),
        patientGuid,
        agencyDoctorGuid: appointmentAgencyDoctorGuid,
        doctorGuid: appointmentDoctorGuid,
        appointmentMethod: method as AppointmentMethod,
        scheduledDate,
        updatedAt: new Date().toString(),
      };

      return new NextAppointment(patientGuid, payload);
    },
    [selectAppointmentMethod],
  );

  const preparedData = (data: IMonthlyInteractionCallValues) => {
    const formData = Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value !== null),
    );

    return {
      isPatientCompleteRpmSMSForm: formData.isPatientCompleteRpmSMSForm,
      additionalEducationalMaterialsSent:
        formData.additionalEducationalMaterialsSent,
      deliveryMethod: formData.deliveryMethod,
      whichPerson: formData.whichPerson,
      otherPerson: formData.otherPerson,
      accessToReading: formData.accessToReading,
      eduAdditionalDetails: formData.eduAdditionalDetails,
      isPatientHaveExpEtc: formData.isPatientHaveExpEtc || '',
      accessToReadingHealtcareCheckbox:
        formData.accessToReadingHealtcareCheckbox,
      accessToReadingHealtcare: formData.accessToReadingHealtcare,
      accessToReadingHealtcareFreeText:
        formData.accessToReadingHealtcareFreeText,
      medicationChangesFreeText: formData.medicationChangesFreeText,
      medicationChangesQuestionsCheckbox:
        formData.medicationChangesQuestionsCheckbox,
      recentUpcomingAppointmentsDate: formData.recentUpcomingAppointmentsDate,
      recentUpcomingAppointmentsTime: formData.recentUpcomingAppointmentsTime,
      recentUpcomingAppointmentsProvider:
        formData.recentUpcomingAppointmentsProvider,
      recentUpcomingAppointmentsProviderGuid:
        formData.recentUpcomingAppointmentsProviderGuid,
      recentUpcomingAppointmentsProviderType:
        formData.recentUpcomingAppointmentsProviderType,
      recentUpcomingAppointmentsText: formData.recentUpcomingAppointmentsText,
      recentUpcomingAppointmentsMethod:
        formData.recentUpcomingAppointmentsMethod,
      recentUpcomingAppointments: formData.recentUpcomingAppointments,
      readingQuestionsRadio: formData.readingQuestionsRadio,
      readingQuestionsText: formData.readingQuestionsText,
      patientGuid,
      educationProvided: formData.educationProvided,
    };
  };
  const onSaveForm = (formData: IMonthlyInteractionCallValues) => {
    const preparedValues = preparedData(formData);
    const filteredValues = Object.fromEntries(
      Object.entries(preparedValues).filter(([_, value]) => value !== null),
    ) as IMonthlyInteractionCallValues;
    const message = generateMonthlyInteractionCallMessage(filteredValues);
    handleSubmit(message);
    closeModal();
  };

  return {
    initialValues,
    onSaveForm,
    selectAppointmentMethod,
    selectAppointmentPayload,
  };
};
